import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { ReactElement, VoidFunctionComponent } from 'react';

import { window } from '../../globals';
import { useDispatchOnMount } from '../../hooks';
import OrganizationModule from '../../redux/organizations';
import { hasError } from '../../utils';
import { ErrorAlert } from '../generic';
import Button from '../generic/Button';
import Card from '../generic/Card/Card';
import Flex from '../generic/Flex';
import List from '../generic/List';
import Loader from '../generic/Loader';

const SSODomainsCard: VoidFunctionComponent = () => {
  const [isCopied, setIsCopied] = useState(false);
  const orgDomains = useSelector(OrganizationModule.selectors.getOrgDomains);

  useDispatchOnMount(OrganizationModule.actions.fetchOrganizationDomains, window.user?.organizationId as number);

  const handleCopyToClipboard = async (): Promise<void> => {
    let domainsInfo = '';
    if (orgDomains.domains.length > 0) {
      domainsInfo = orgDomains.domains.map(({ domain, ssoMethod }) => `${domain}: ${ssoMethod}`).join('\n');
    } else {
      domainsInfo = 'None';
    }

    await navigator.clipboard.writeText(domainsInfo);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  let content: ReactElement;
  if (hasError(orgDomains.status)) {
    content = <ErrorAlert status={orgDomains.status} withMargin />;
  } else if (orgDomains.status.loading) {
    content = <Loader />;
  } else if (orgDomains.domains.length === 0) {
    content = <p className="margin-left-md margin-bottom-md">None</p>;
  } else {
    content = (
      <List isBorderless>
        <List.Row hasTightPadding hasTightVerticalPadding left={<b>Domain</b>} right={<b>SSO Method</b>} />
        {orgDomains.domains.map(({ domain, ssoMethod }) => (
          <List.Row key={domain} hasTightPadding hasTightVerticalPadding left={domain} right={ssoMethod} />
        ))}
      </List>
    );
  }

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>
        <Flex direction="row" align="center" justify="space-between">
          <div>SSO Domains Claimed</div>
          <Button onClick={handleCopyToClipboard} size="micro" type="outline">
            {isCopied ? 'Copied!' : 'Copy'}
          </Button>
        </Flex>
      </Card.Header>
      <Card.Content>
        {content}
      </Card.Content>
    </Card>
  );
};

export default SSODomainsCard;
